// src/lib/broker/normalizedPnl.js
//
// EF-156: Single source of truth for position-level cost basis and unrealized P&L.
//
// Reads the normalized broker_positions row shape (Mirror principle). Returns the
// authoritative cost basis and gain/loss for display. Compliance code calls this
// helper without knowing which broker produced the row — that's the contract.
//
// Preference order (descending authority):
//   1. liveMktVal supplied → recompute G/L = liveMktVal - costBasis;
//      pnlSource = 'live_derived'. (Used by IBKR live-tick path between Schwab syncs.)
//   2. cost_basis_source === 'broker_open_pnl' → use unrealized_pnl_long + unrealized_pnl_short;
//      pnlSource = 'broker_open_pnl'.
//   3. cost_basis_source === 'broker_cost_only' → derive G/L from market_value - costBasis;
//      pnlSource = 'broker_cost_only'.
//   4. fallback (cost_basis_source === 'derived' or null) → average_price × |qty| × multiplier;
//      pnlSource = 'derived'.
//
// Cost basis selection (independent of P&L selection):
//   - broker_open_pnl implied basis (market_value - open P&L) for long Schwab rows
//   - cost_basis_long if qty >= 0 and present
//   - cost_basis_short if qty < 0 and present
//   - cost_basis_long if qty < 0 and only long is present (Schwab edge case — explicit fallback)
//   - average_price × |qty| × contract_multiplier otherwise
//
// IMPORTANT — diagnostic fields:
//   The returned `costSource` and `pnlSource` are diagnostic. Callers MUST NOT
//   branch on them for business logic. They exist for the audit log and the
//   "click → see why this number was picked" UI affordance only.
//
// IMPORTANT — multiplier contract:
//   This helper NEVER multiplies cost basis by quantity or contract multiplier.
//   The sync-layer mapper (e.g. normalizeSchwabPosition) is responsible for
//   storing dollar cost basis (per-share × |qty| × multiplier) in cost_basis_long
//   / cost_basis_short. The helper trusts the stored value verbatim. Only the
//   legacy `average_price` fallback path multiplies, because legacy rows store
//   per-share. Future broker adapters (IBKR, manual upload) MUST follow the
//   "store dollar cost basis at the sync layer" contract.
//
// Sign convention (Schwab):
//   The EF-156 probe (2026-05-10) observed `longOpenProfitLoss` as positive for
//   winning longs and negative for losing longs (MSTR sample: -546.92 on a
//   position underwater). No real short positions were present in the probe
//   dataset, so `shortOpenProfitLoss` sign is assumed by symmetry. AR-MB-04
//   owner must re-verify on the first real short.
//
// FIXME-shorts (unverified convention for short positions):
//   The helper's `broker_cost_only`, `live_derived`, and `derived` branches use
//   `gainLoss = mktVal - costBasis` for all rows. This is correct for longs and
//   economically inverted for shorts (a short profits when costBasis > |mktVal|).
//   The bug is masked today because:
//     - Schwab populates unrealized_pnl_short on every short row → broker_open_pnl
//       branch wins → no math performed.
//     - Brandon has no current short positions in any Schwab account.
//   When the first real short lands (Brandon opens a covered call / cash-secured
//   put, OR AR-MB-04 ships IBKR shorts via broker_cost_only), do the following:
//     1. Probe Schwab's stored market_value sign for the short — is it negative
//        (signed) or absolute? Erevis's live recompute (lastPrice × qty × mul) IS
//        signed, so the live_derived branch needs `qty < 0 ? costBasis + val : val - costBasis`.
//        The broker_cost_only branch may need the same OR `costBasis - Math.abs(val)`
//        depending on Schwab's convention.
//     2. Update test case 6 in normalizedPnl.test.js to assert the economically
//        correct value (currently asserts -1500; should be +100 once verified).
//     3. Add new test cases for short option live tick (the actual scenario that
//        will hit Brandon's display).
//     4. Fix the parent-group total in PositionsCurrent.jsx (see FIXME-shorts there).
//
// Forward placeholders (NOT implemented this commit):
//   - AR-MB-04: when last_updated_at is older than freshnessMs threshold AND
//     liveMktVal is supplied, bypass broker_open_pnl regardless. The contract
//     accepts last_updated_at on the input row already; the threshold logic
//     lands in AR-MB-04 once IBKR's near-tick broker P&L is real.
//   - AR-MB-06: when currency on the row is non-USD, callers (or a layer above
//     this helper) are responsible for FX-normalizing market_value and liveMktVal
//     before passing in. This helper is currency-blind; it asserts only that
//     all numeric inputs share the same currency.

export function derivePositionPnl(pos, { liveMktVal } = {}) {
  const qty       = Number(pos?.quantity) || 0
  const absQty    = Math.abs(qty)
  const mktVal    = Number(pos?.market_value) || 0
  const hasMktVal = pos?.market_value != null && Number.isFinite(Number(pos.market_value))
  const mul       = Number(pos?.contract_multiplier)
                  || (pos?.instrument_type === 'OPTION' ? 100 : 1)
  const cbLong    = pos?.cost_basis_long  == null ? NaN : Number(pos.cost_basis_long)
  const cbShort   = pos?.cost_basis_short == null ? NaN : Number(pos.cost_basis_short)
  const avgLegacy = Number(pos?.average_price) || 0
  const rawLongPnl = pos?.raw?.longOpenProfitLoss == null ? null : Number(pos.raw.longOpenProfitLoss)
  const rawShortPnl = pos?.raw?.shortOpenProfitLoss == null ? null : Number(pos.raw.shortOpenProfitLoss)
  const longPnl   = pos?.unrealized_pnl_long  == null ? rawLongPnl : Number(pos.unrealized_pnl_long)
  const shortPnl  = pos?.unrealized_pnl_short == null ? rawShortPnl : Number(pos.unrealized_pnl_short)
  const hasBrokerPnl = longPnl != null || shortPnl != null
  const source    = hasBrokerPnl
                      ? 'broker_open_pnl'
                      : (pos?.cost_basis_source || (Number.isFinite(cbLong) || Number.isFinite(cbShort)
                          ? 'broker_cost_only'
                          : 'derived'))

  // ── Cost basis ───────────────────────────────────────────────────────────
  let costBasis = 0
  let costSource = 'derived'
  if (source === 'broker_open_pnl' && qty >= 0 && longPnl != null && Number.isFinite(longPnl) && hasMktVal) {
    const impliedBasis = Math.round((mktVal - longPnl) * 100) / 100
    if (Number.isFinite(impliedBasis) && impliedBasis > 0) {
      costBasis = impliedBasis
      costSource = 'broker_open_pnl_implied'
    } else if (Number.isFinite(cbLong) && cbLong > 0) {
      costBasis = cbLong
      costSource = 'cost_basis_long'
    } else {
      costBasis = avgLegacy * absQty * mul
      costSource = 'legacy_average_price'
    }
  } else if (Number.isFinite(cbLong) && cbLong > 0 && qty >= 0) {
    costBasis = cbLong
    costSource = 'cost_basis_long'
  } else if (Number.isFinite(cbShort) && cbShort > 0 && qty < 0) {
    costBasis = cbShort
    costSource = 'cost_basis_short'
  } else if (Number.isFinite(cbLong) && cbLong > 0) {
    // Long basis present, qty sign mismatch — explicit fallback. Better than legacy avg.
    costBasis = cbLong
    costSource = 'cost_basis_long'
  } else {
    costBasis = avgLegacy * absQty * mul
    costSource = 'legacy_average_price'
  }

  // ── Gain / loss ──────────────────────────────────────────────────────────
  let gainLoss = 0
  let pnlSource

  if (liveMktVal != null && Number.isFinite(Number(liveMktVal))) {
    // Live-tick path: bypass broker P&L (it's stale between syncs).
    // FIXME-AR-MB-04: blind bypass is correct only while broker P&L is 15-min stale (Schwab).
    // Revisit when IBKR's near-tick unrealized_pnl_* lands — at that point this bypass needs
    // a freshness threshold (read from last_updated_at) so we don't override a fresh broker
    // P&L value with a redundant live-derived recompute.
    // FIXME-shorts: gainLoss formula below is wrong for qty < 0. See helper docstring.
    gainLoss = Number(liveMktVal) - costBasis
    pnlSource = 'live_derived'
  } else if (source === 'broker_open_pnl' && (longPnl != null || shortPnl != null)) {
    gainLoss = (longPnl || 0) + (shortPnl || 0)
    pnlSource = 'broker_open_pnl'
  } else if (source === 'broker_cost_only' || costSource !== 'legacy_average_price') {
    // FIXME-shorts: gainLoss formula below is wrong for qty < 0. See helper docstring.
    gainLoss = mktVal - costBasis
    pnlSource = 'broker_cost_only'
  } else {
    // FIXME-shorts: gainLoss formula below is wrong for qty < 0. See helper docstring.
    gainLoss = mktVal - costBasis
    pnlSource = 'derived'
  }

  const gainLossPct = costBasis > 0 ? (gainLoss / costBasis) * 100 : 0

  return { costBasis, gainLoss, gainLossPct, costSource, pnlSource }
}
